/**
 * RFC-108-G alternate-group crossfade (Phase 2).
 *
 * Generalises `lorCrossfade.ts` from "fade PLATEAU-patched materials in the
 * whole scene" to "fade the materials owned by ONE layer". Used when the
 * operator switches alternates in the layer panel (e.g. bldg LOD2 → LOD1).
 *
 * Contract:
 *
 *   crossfadeBetweenLayers(adapter, fromLayerId, toLayerId)
 *     1. Fade out the outgoing layer's materials 1 → 0 over `durationMs`.
 *     2. While that is running, fade in the incoming layer's materials
 *        0 → 1 over the same duration (mirror animation).
 *     3. When the fade-out completes, hide the outgoing layer (engine-level
 *        `setLayerVisible(false)`) so the unmount-on-hide semantics of 3D
 *        Tiles kicks in and the tileset no longer occupies memory.
 *
 * The crossfade uses `requestAnimationFrame` for time keeping and uses the
 * same ease-in-out curve as `lorCrossfade.ts`. Materials are restored to
 * `transparent: false` once the in-fade settles at 1.0 so the GPU does not
 * keep paying the alpha-blend cost at rest.
 *
 * Tests stub `requestAnimationFrame` to fire one frame with a far-future
 * timestamp so the animation always converges in a single tick.
 *
 * NOTE: this module deliberately stays adapter-shape-agnostic — it accepts
 * a small adapter "facade" rather than the full Giro3DAdapter class so it
 * can be unit tested without a Giro3D instance.
 */

import * as THREE from 'three';

export const ALTERNATE_CROSSFADE_DURATION_MS = 400;

// ---------------------------------------------------------------------------
// Adapter facade — the minimum surface this module needs
// ---------------------------------------------------------------------------

export interface AlternateCrossfadeAdapter {
  /**
   * Return the Three.js Object3D for the given layer (the entity registered
   * in the adapter's internal `layers` map). May be `null` when the layer
   * is not mounted (the crossfade is then a no-op for that side).
   */
  getLayerObject3D(layerId: string): THREE.Object3D | null;
  /**
   * Tell the adapter to hide a layer at the engine level. For 3D Tiles this
   * triggers the standard hide-3D-Tiles-unmount path (no CRS/camera side
   * effects, frees memory). Optional: when absent the crossfade only fades
   * opacities and the caller is responsible for hiding.
   */
  setLayerVisible?(layerId: string, visible: boolean): Promise<void> | void;
  /**
   * Tell the renderer the scene changed; Giro3D adapters expose
   * `notifyChange()` directly. Optional.
   */
  notifyChange?(): void;
}

// ---------------------------------------------------------------------------
// Material walks
// ---------------------------------------------------------------------------

function collectMaterials(root: THREE.Object3D | null): THREE.Material[] {
  if (!root) return [];
  const result: THREE.Material[] = [];
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const mat of mats) {
      if (mat) result.push(mat);
    }
  });
  return result;
}

const ease = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t);

interface AnimationHandle {
  cancel(): void;
  /** Resolves when the animation reaches t = 1. */
  done: Promise<void>;
}

function animateOpacity(
  materials: THREE.Material[],
  fromOpacity: number,
  toOpacity: number,
  durationMs: number,
  onChange?: () => void,
): AnimationHandle {
  if (materials.length === 0) {
    return { cancel: () => {}, done: Promise.resolve() };
  }
  for (const mat of materials) {
    mat.transparent = true;
    mat.opacity = fromOpacity;
  }
  let cancelled = false;
  const start = performance.now();
  let resolveDone: (() => void) | null = null;
  const done = new Promise<void>((resolve) => {
    resolveDone = resolve;
  });
  const step = (time: number) => {
    if (cancelled) return;
    const t = Math.min(1, (time - start) / durationMs);
    const k = ease(t);
    const current = fromOpacity + (toOpacity - fromOpacity) * k;
    for (const mat of materials) {
      mat.opacity = current;
      mat.needsUpdate = true;
      if (t >= 1 && toOpacity >= 1) {
        // Restore non-translucent rendering at rest.
        mat.transparent = false;
      }
    }
    onChange?.();
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      resolveDone?.();
    }
  };
  requestAnimationFrame(step);
  return {
    cancel: () => {
      cancelled = true;
    },
    done,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface CrossfadeOptions {
  durationMs?: number;
  /**
   * When `true` (default), the outgoing layer's `setLayerVisible(false)` is
   * called when the fade-out completes. Setting `false` keeps the layer
   * mounted at zero opacity — useful for the alt-group case where we want
   * the same layer back instantly if the user toggles within a few ms.
   */
  hideOutgoingOnComplete?: boolean;
}

/**
 * Crossfade between two alternate layers of the same RFC-108-G group.
 *
 * Returns a promise that resolves when **both** sides of the crossfade have
 * settled. Callers can `await` it for chained UI behaviour (e.g. focus the
 * incoming layer in the tree).
 */
export async function crossfadeBetweenLayers(
  adapter: AlternateCrossfadeAdapter,
  fromLayerId: string | null,
  toLayerId: string | null,
  options: CrossfadeOptions = {},
): Promise<void> {
  const duration = options.durationMs ?? ALTERNATE_CROSSFADE_DURATION_MS;
  const hideOutgoing = options.hideOutgoingOnComplete !== false;

  const fromRoot = fromLayerId ? adapter.getLayerObject3D(fromLayerId) : null;
  const toRoot = toLayerId ? adapter.getLayerObject3D(toLayerId) : null;

  const fromMaterials = collectMaterials(fromRoot);
  const toMaterials = collectMaterials(toRoot);

  const notify = () => adapter.notifyChange?.();

  const animations: Promise<void>[] = [];
  if (fromMaterials.length > 0) {
    animations.push(animateOpacity(fromMaterials, 1, 0, duration, notify).done);
  }
  if (toMaterials.length > 0) {
    animations.push(animateOpacity(toMaterials, 0, 1, duration, notify).done);
  }

  await Promise.all(animations);

  if (hideOutgoing && fromLayerId && adapter.setLayerVisible) {
    try {
      await adapter.setLayerVisible(fromLayerId, false);
    } catch (error) {
      console.warn(
        `[alternateCrossfade] outgoing setLayerVisible(${fromLayerId}, false) failed:`,
        error,
      );
    }
  }
}

/**
 * Fade a single layer from full opacity to invisible (no crossfade partner).
 * Used when an alternate group is toggled off entirely.
 */
export async function fadeOutLayer(
  adapter: AlternateCrossfadeAdapter,
  layerId: string,
  durationMs: number = ALTERNATE_CROSSFADE_DURATION_MS,
): Promise<void> {
  const root = adapter.getLayerObject3D(layerId);
  const materials = collectMaterials(root);
  if (materials.length === 0) return;
  await animateOpacity(materials, 1, 0, durationMs, () => adapter.notifyChange?.()).done;
}
